// frontend/src/views/DocumentList.tsx

import React, { useState, useCallback, useMemo } from "react";
import { FileText, ImageIcon, Search, Eye } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Document } from "../services/api";
import { useDocuments } from "../contexts/DocumentsContext";

const getAllTags = (documents: Document[]): string[] => {
  const tags = new Set<string>();
  documents.forEach((doc) => {
    doc.tags.forEach((tag) => tags.add(tag));
  });
  return Array.from(tags);
};

const DocumentRow = React.memo(
  ({
    doc,
    isSelected,
    onSelect,
    onNavigate,
  }: {
    doc: Document;
    isSelected: boolean;
    onSelect: (id: number) => void;
    onNavigate: (id: number) => void;
  }) => {
    const handleCardClick = (e: React.MouseEvent) => {
      if (!(e.target instanceof HTMLInputElement)) {
        onNavigate(doc.id);
      }
    };

    return (
      <div
        className="p-4 bg-white rounded-lg shadow hover:shadow-md transition-shadow cursor-pointer"
        onClick={handleCardClick}
      >
        <div className="flex items-center gap-3 md:gap-4">
          <div className="flex items-center gap-2 md:gap-3">
            <div onClick={(e) => e.stopPropagation()}>
              <input
                type="checkbox"
                checked={isSelected}
                onChange={() => onSelect(doc.id)}
                className="w-4 h-4 md:w-5 md:h-5 border-2 rounded text-blue-600 focus:ring-blue-500"
              />
            </div>
            <div className="w-12 h-12 md:w-16 md:h-16 bg-gray-100 rounded-lg flex items-center justify-center">
              {doc.type === "pdf" ? (
                <div className="text-center">
                  <FileText size={20} className="text-gray-400 mx-auto md:size-6" />
                  <span className="text-xs text-gray-400 mt-1 hidden sm:block">PDF</span>
                </div>
              ) : (
                <div className="text-center">
                  <ImageIcon size={20} className="text-gray-400 mx-auto md:size-6" />
                  <span className="text-xs text-gray-400 mt-1 hidden sm:block">IMG</span>
                </div>
              )}
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <h3 className="font-medium truncate">{doc.title}</h3>
            <div className="text-sm text-gray-500 truncate">{doc.filename}</div>
            <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-1 sm:gap-x-4 sm:gap-y-2 mt-1">
              <span className="text-sm text-gray-500">
                {new Date(doc.exam_date).toLocaleDateString()}
              </span>
              <div className="flex gap-1 sm:gap-2 flex-wrap">
                {doc.tags?.slice(0, 3).map((tag: string) => (
                  <span
                    key={tag}
                    className="px-2 py-0.5 bg-gray-100 rounded-full text-xs text-gray-600"
                  >
                    {tag}
                  </span>
                ))}
                {doc.tags?.length > 3 && (
                  <span className="px-2 py-0.5 bg-gray-200 rounded-full text-xs text-gray-500">
                    +{doc.tags.length - 3}
                  </span>
                )}
              </div>
            </div>
          </div>

          <Eye size={20} className="text-gray-500 shrink-0 md:size-6" />
        </div>
      </div>
    );
  }
);

const DocumentList = () => {
  const navigate = useNavigate();
  const { documents, loading, error, sortDirection, setSortDirection } =
    useDocuments();

  const [selectedDocs, setSelectedDocs] = useState<number[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  const availableTags = useMemo(() => getAllTags(documents), [documents]);

  const filteredDocs = useMemo(() => {
    return documents.filter((doc: Document) => {
      const matchesSearch =
        searchTerm === "" ||
        doc.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        doc.filename.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesTags =
        selectedTags.length === 0 ||
        selectedTags.some((tag) => doc.tags.includes(tag));

      return matchesSearch && matchesTags;
    });
  }, [documents, searchTerm, selectedTags]);

  const handleSelect = useCallback((docId: number): void => {
    setSelectedDocs((prev) => {
      if (prev.includes(docId)) {
        return prev.filter((id) => id !== docId);
      }
      if (prev.length < 3) {
        return [...prev, docId];
      }
      return prev;
    });
  }, []);

  const handleNavigate = useCallback(
    (docId: number): void => {
      navigate(`/documents/${docId}`);
    },
    [navigate]
  );

  const toggleTag = useCallback((tag: string): void => {
    setSelectedTags((prev: string[]) =>
      prev.includes(tag)
        ? prev.filter((t: string) => t !== tag)
        : [...prev, tag]
    );
  }, []);

  const handleCompare = useCallback(() => {
    const path = `/documents/compare/${selectedDocs.join(",")}`;
    navigate(path);
  }, [navigate, selectedDocs]);

  const handleSortDirectionChange = useCallback(() => {
    setSortDirection((prev) => (prev === "DESC" ? "ASC" : "DESC"));
  }, [setSortDirection]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2">Caricamento documenti...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full max-w-4xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-4xl mx-auto p-6">
      <h1 className="text-2xl font-bold mb-8">Documenti Disponibili</h1>

      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="flex-1">
          <div className="relative">
            <Search
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              size={20}
            />
            <input
              type="text"
              placeholder="Cerca documenti..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 w-full border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <div className="flex gap-2">
          <button
            onClick={handleSortDirectionChange}
            className="px-4 py-2 border rounded-lg hover:bg-gray-50"
          >
            {sortDirection === "DESC"
              ? "Più Recenti prima"
              : "Più Vecchi prima"}
          </button>
        </div>

        <button
          disabled={selectedDocs.length < 2}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg disabled:opacity-50 whitespace-nowrap"
          onClick={handleCompare}
        >
          Confronta ({selectedDocs.length}/3)
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {availableTags.map((tag) => (
          <button
            key={tag}
            onClick={() => toggleTag(tag)}
            className={`px-3 py-1 rounded-lg text-sm border ${
              selectedTags.includes(tag)
                ? "bg-blue-600 text-white border-blue-600"
                : "border-gray-300 hover:border-blue-500"
            }`}
          >
            {tag}
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {filteredDocs.map((doc) => (
          <DocumentRow
            key={doc.id}
            doc={doc}
            isSelected={selectedDocs.includes(doc.id)}
            onSelect={handleSelect}
            onNavigate={handleNavigate}
          />
        ))}

        {filteredDocs.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            Nessun documento trovato
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentList;
